import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Mail, Lock, Boxes } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { apiError } from "@/lib/api";

const HERO = "https://images.unsplash.com/photo-1764795850513-fa8a469a4600?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400";

function IconInput({ icon: Icon, ...props }) {
  return (
    <div className="relative">
      <Icon className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
      <Input {...props} className="h-12 pl-10" />
    </div>
  );
}

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ email: "", password: "" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      await login(form.email, form.password);
      navigate("/");
    } catch (err) {
      setError(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="grid min-h-screen lg:grid-cols-[1.1fr_1fr]">
      <div className="relative hidden overflow-hidden bg-slate-900 lg:block">
        <img src={HERO} alt="Entrepôt" className="absolute inset-0 h-full w-full object-cover opacity-50" />
        <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/40 to-transparent" />
        <div className="relative flex h-full flex-col justify-between p-12 text-white">
          <div className="flex items-center gap-3">
            <div className="flex h-11 w-11 items-center justify-center rounded bg-orange-500 text-slate-950"><Boxes className="h-6 w-6" /></div>
            <span className="font-display text-3xl font-extrabold uppercase tracking-wide">StockKit</span>
          </div>
          <div className="max-w-md">
            <p className="font-display text-5xl font-extrabold uppercase leading-[0.95]">Chaque pièce,<br />chaque kit,<br /><span className="text-orange-400">au bon endroit.</span></p>
            <p className="mt-5 text-slate-300">Articles, kits virtuels, mouvements et alertes de seuil — un stock juste, sans calcul de valeur.</p>
          </div>
        </div>
      </div>
      <div className="flex items-center justify-center bg-white px-6 py-12 sk-grid-bg">
        <div className="w-full max-w-md rounded-md border bg-white p-8 shadow-sm sk-rise">
          <h1 className="font-display text-4xl font-extrabold uppercase text-slate-900">Connexion</h1>
          <p className="mb-6 mt-1 text-sm text-slate-500">Accédez à votre espace de gestion de stock.</p>
          <form onSubmit={submit} className="space-y-4">
            <div className="space-y-1.5"><Label>E-mail</Label><IconInput icon={Mail} type="email" required value={form.email} onChange={set("email")} placeholder="vous@entreprise.fr" data-testid="auth-email-input" /></div>
            <div className="space-y-1.5"><Label>Mot de passe</Label><IconInput icon={Lock} type="password" required value={form.password} onChange={set("password")} placeholder="••••••••" data-testid="auth-password-input" /></div>
            {error && <div className="rounded border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700" data-testid="auth-error">{error}</div>}
            <Button type="submit" disabled={busy} className="h-12 w-full text-base" data-testid="auth-submit-button">{busy ? "Veuillez patienter…" : "Se connecter"}</Button>
          </form>
          <p className="mt-6 text-xs text-slate-500">Pas de compte ? Les comptes sont créés par un administrateur.</p>
        </div>
      </div>
    </div>
  );
}
